import React, { useState } from 'react';
import { Anchor, Button } from '@mantine/core';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ArrowLeft } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import CreationWizardCard from './CreationWizardCard';
import * as classes from '../../../styles/animations.module.css';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';

const CreationWizard = ({
  loading,
  submitting,
  steps,
  isSportsHeadzAdmin,
  formState,
  setFormState,
  createOptions,
  maxStackSize,
  onCreate
}) => {
  const { isLargeMobileOrSmaller } = useMediaQueryIndex();
  const [stepState, setStepState] = useState({
    index: 0,
    direction: 'forward',
    hasSkipped: false
  });
  const visibleSteps = steps.filter(
    (step) => !step.isVisible || step.isVisible(formState, isSportsHeadzAdmin)
  );
  const maxVisibleStack = maxStackSize || 2;

  const onContinue = () => {
    if (stepState.index < visibleSteps.length - 1) {
      setStepState({
        ...stepState,
        direction: 'forward',
        index: stepState.index + 1
      });
    }
  };

  const onBack = () => {
    if (stepState.hasSkipped) {
      setStepState({
        ...stepState,
        direction: 'back',
        index: 0,
        hasSkipped: false
      });
    }
    else if (stepState.index > 0) {
      setStepState({
        ...stepState,
        direction: 'back',
        index: stepState.index - 1
      });
    }
  };

  const onCreateResource = () => {
    onCreate(formState, { hasSkipped: stepState.hasSkipped });
  };

  return (
    <AppStack style={{ flex: 1, width: '100%', minHeight: '100vh' }}>
      <AppStack
        style={{
          flex: 1,
          height: '100%',
          paddingTop: isLargeMobileOrSmaller ? 60 : 80,
          alignItems: 'center'
        }}
      >
        <AppStack
          style={{
            flex: 1,
            gap: 40,
            width: '100%',
            maxWidth: 800,
            alignItems: 'start',
            position: 'relative'
          }}
        >
          <Button
            color="white"
            component={Link}
            leftSection={<ArrowLeft size={18} />}
            to="/merchant/stores/switch"
            variant="outline"
            visibleFrom="xsm"
          >
            Cancel
          </Button>

          <AppStack
            style={{
              gap: 0,
              width: '100%',
              height: 'auto',
              position: 'relative'
            }}
          >
            {visibleSteps.map((step, i) => {
              const isActive = i === stepState.index;
              const isBefore = i < stepState.index;
              const isReturning =
                stepState.direction === 'back' && i === stepState.index;
              const isRestacking =
                stepState.direction === 'back' && i === stepState.index + 1;

              const depth = i - stepState.index;
              const isHidden = depth > maxVisibleStack;
              const zIndex = isActive || isReturning ? 999 : steps.length - i;

              return (
                <AppStack
                  key={step.value}
                  className={classes.stackCardContainer}
                  style={{ paddingBottom: 60 }}
                >
                  <CreationWizardCard
                    className={[
                      classes.stackCard,
                      isActive ? classes.stackCardActive : '',
                      stepState.direction === 'forward' && isBefore
                        ? classes.dismissed
                        : '',
                      isReturning ? classes.returning : '',
                      isRestacking ? classes.restacking : ''
                    ].join(' ')}
                    description={step.description}
                    onBack={stepState.index > 0 ? onBack : null}
                    onContinue={
                      stepState.index < visibleSteps.length - 1
                        ? onContinue
                        : null
                    }
                    onCreate={
                      stepState.index === visibleSteps.length - 1
                        ? onCreateResource
                        : null
                    }
                    style={{
                      display: isHidden ? 'none' : 'block', // <-- hide if too deep
                      zIndex,
                      '--offset': `${(i - stepState.index) * -10}%`,
                      '--scale': 1 - Math.max(0, i - stepState.index) * 0.08,
                      '--opacity':
                        i < stepState.index
                          ? 0 // hidden unless returning
                          : 1 - (i - stepState.index) * 0.02
                    }}
                    submitting={submitting}
                    title={step.title}
                  >
                    <step.Component
                      createOptions={createOptions}
                      formState={formState}
                      isSportsHeadzAdmin={isSportsHeadzAdmin}
                      loading={loading || !createOptions}
                      setFormState={setFormState}
                      submitting={submitting}
                    />
                  </CreationWizardCard>

                  {stepState.index === 0 && i === 0 && (
                    <AppStack style={{ padding: '0px 20px' }}>
                      <AppFlexbox>
                        <Anchor
                          onClick={() => {
                            setStepState({
                              ...stepState,
                              index: visibleSteps.length - 1,
                              direction: 'forward',
                              hasSkipped: true
                            });
                          }}
                          style={{
                            color: '#FFF',
                            fontSize: 14
                          }}
                        >
                          Skip customized setup
                        </Anchor>
                      </AppFlexbox>
                    </AppStack>
                  )}
                </AppStack>
              );
            })}
          </AppStack>
        </AppStack>
      </AppStack>
    </AppStack>
  );
};

CreationWizard.propTypes = {
  formState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  steps: PropTypes.array.isRequired,
  createOptions: PropTypes.object,
  isSportsHeadzAdmin: PropTypes.bool,
  loading: PropTypes.bool,
  maxStackSize: PropTypes.number,
  onCreate: PropTypes.func,
  submitting: PropTypes.bool
};

export default CreationWizard;
